<script>
import { widgetActions, zendeskActions } from '@state/helpers'
import { mapGetters, mapState } from 'vuex'
import axios from 'axios'
import Raven from 'raven-js'

import Layout from '@layouts/main'
import zendesk from '@mixins/zendesk-utils.js'
import commomUtils from '@mixins/common-utils.js'
import samUtils from '@mixins/sam-utils.js'
import deepLink from '@mixins/deeplink.js'
import fallbackScreens from '@mixins/screens.js'
import ENV from '@src/env'
import ErrorComponent from '@components/error-component'
import GTM_CONSTANTS from '@constants/google-tag-manager'
import AMPLITUDE from '@constants/amplitude'
import { getScreenWidgets, getToHide } from './utils'
import { keysToCamel, getPropsFromObject } from '../../utils/object-utils'

export default {
  page: {
    title: 'Widgets',
    meta: [{ name: 'description', content: 'Widgets' }],
  },
  components: {
    Layout,
    ErrorComponent,
  },
  mixins: [zendesk(), commomUtils(), samUtils(), deepLink(), fallbackScreens()],
  loadChatAPI: true,
  data() {
    return {
      loading: false,
      error: null,
      componentList: [],
      params: {},
      userCountry: '',
      viewEvent: 'VIEW_ARTICLE',
      toHide: [],
    }
  },
  computed: {
    ...mapState('widgets', {
      user: state => state.data.SharedData.userData,
      common: state => state.common,
    }),
    ...mapState('zendesk', {
      fetching: state => state.fetching,
    }),
    ...mapGetters('widgets', [
      'userEnv',
      'articleBuilderBody',
      'logsUserID',
      'userType',
    ]),
    logsUserId() {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: `${this.$route.query.country_code.toUpperCase()}_${
          this.logsUserID
        }`,
      })
      return user
    },
    userForLogs() {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: this.logsUserID,
      })
      delete user.token
      return user
    },
    payloadForlogs() {
      const { articleId } = this.$route.params
      const logKeys = [
        'country_code',
        'language',
        'order_id',
        'model_name',
        'origin_transaction',
        'payment_amount',
        'transaction_id',
        'tag',
      ];

      const selectedPayload = keysToCamel(getPropsFromObject(this.$route.query, logKeys));
      const payload = Object.assign({}, this.$route.params, selectedPayload, { articleId })
      delete payload.token
      delete payload.store_type
      delete payload.enable_webkit
      delete payload.dev

      return payload
    },
    requestScreenUrl() {
      const { order_id } = this.$route.query // eslint-disable-line
      const orderExist = order_id && Number(order_id) > 1000 // eslint-disable-line

      return orderExist
        ? '/api/support-flows/screens/build-article'
        : '/api/support-flows/screens/build-article-no-order'
    },
    isUserRT() {
      return this.userType === 'storekeepers'
    },
  },
  watch: {
    $route: 'fetchArticleScreen',
  },
  beforeCreate() {
    const validLAnguages = ['es', 'en', 'pt']
    const { language: queryLanguage } = this.$route.query
    const browserLanguage =
      window.navigator.userLanguage || window.navigator.language
    const language = queryLanguage || browserLanguage
    const [matchLanguage] = validLAnguages.filter(l =>
      language.toLowerCase().includes(l)
    )
    const lang = matchLanguage || browserLanguage

    this.$root._i18n._vm.locale = lang
    axios.defaults.headers.post['Accept-Language'] = lang
  },
  async created() {
    try {
      // report entrance for g-analytics
      this.$ga.page(this.$router)
      this.initStore()
      const { articleId } = this.$route.params
      this.params = Object.assign({}, this.$route.query, {
        screen_id: articleId,
      })
      this.validateParams(this.params)
      await this.fetchArticleScreen()
    } catch (err) {
      this.error = err.toString()
    }
  },
  methods: {
    ...zendeskActions,
    ...widgetActions,
    initStore() {
      const { country_code: countryCode, token } = this.$route.query
      this.commitUser({
        countryCode,
        token,
        os: this.getMobileOperatingSystem(),
      })
      this.commitParams(this.$route.params)
      this.commitQuery(this.$route.query)
    },
    async fetchArticleScreen() {
      this.loading = true

      try {
        const { isguest = false } = this.params
        const authOwner = isguest ? 'application_guest' : 'application_user'
        const { data, status } = await axios.post(
          `${ENV.rappi.url[this.userEnv]}${this.requestScreenUrl}`,
          this.articleBuilderBody,
          {
            headers: {
              Authorization: `Bearer ${this.params.token}`,
              'auth-owner': authOwner,
            },
            validateStatus: function(status) {
              return status >= 200 && status < 400 // default
            },
          }
        )


        this.loading = false

        if (status === 302) {
          this.$router.push({
            name: 'zendesk-articles',
            query: this.$route.query,
            params: { articleId: data.article_id },
          })
        } else {
          const { screenWidgets, isGuestArticle = false } = data.data
          const evaluateGuest = isGuestArticle && isguest
          this.setGuestArticle(evaluateGuest)
          this.componentList = getScreenWidgets(screenWidgets)
          this.setToHide()
          this.componentList.map(c => this.commitWidgetData(c))
          this.dispatchEventTrack(this.viewEvent)
        }
      } catch (err) {
        this.loading = false
        Raven.captureException(err)
        const { status } = err.response
        this.dispatchEventTrack(
          AMPLITUDE.ERROR_FETCHING_ARTICLE,
          JSON.parse(JSON.stringify(err))
        )
        if (status >= 500 && status < 600) this.callDeepLink('goto=home')
        else this.error = err.toString()
        this.$noty.error(this.error, {
          timeout: 3000,
          layout: 'topCenter',
          progressBar: false,
          modal: true,
          theme: 'relax',
        })
      }
    },
    getComponent({ component }) {
      return component()
    },
    setToHide() {
      this.componentList.map(item => {
        this.commitWidgetChanges({
          widgetName: item.data.customFieldId,
          data: {
            show: true,
            required: true,
          },
        })
      })
      this.toHide = getToHide(this.componentList)
      for (let j = 0; j < this.toHide.length; j++) {
        const { toEvaluate } = this.toHide[j]
        const values = Object.values(toEvaluate);
        for (let i = 0; i < values.length; i++) {
          this.commitWidgetChanges({
            widgetName: values[i],
            data: {
              show: false,
              required: false,
            },
          })
        }
      }
    },
    onChildChange(childData) {},
    dispatchEventTrack(eventName, payload = this.payloadForlogs) {
      this.$gtm.trackEvent({
        event: GTM_CONSTANTS.LOG_EVENT,
        noninteraction: eventName,
        payload,
        eventType: eventName,
        user: this.userForLogs,
      })
    },
    validQueryLanguage(language) {
      const validLAnguages = ['ES', 'EN', 'PT']
      if (validLAnguages.includes(language.toUpperCase())) return language
      return window.navigator.userLanguage || window.navigator.language
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.container">
      <div
        v-if="loading || fetching"
        :class="$style.container__loading">
        <v-progress-circular
          :width="3"
          :class="[{ [$style.progressRT]: isUserRT }, $style.progress]"
          ss-circular
          indeterminate
        />
      </div>

      <div
        v-else-if="error"
        :class="$style.container__error">
        <ErrorComponent :text="$t('COMMON.ERROR_MESSAGE')"/>
      </div>
      <div v-else>
        <div
          v-for="component in componentList"
          :key="component.key"
          :id="component.label"
        >
          <component
            :class="$style.component__content"
            :is="getComponent(component)"
            :token="params.token"
            :component-data="JSON.parse(JSON.stringify(component.data))"
            :user-country="userEnv"
            :to-hide="toHide"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss" module>
@import '@design';
.container {
  &__loading {
    height: 100vh;
    padding-top: 10rem;
    color: $primary-green;
    background: $color-body-bg;
  }

  &__error {
    height: 100vh;
    padding-top: 10rem;
  }

  &__spacer {
    &Small {
      height: 1px;
    }

    &Medium {
      height: 6rem;
    }

    &Big {
      height: 1px;
    }
  }
}
.component {
  width: auto;

  &:last-child {
    width: 0;
    height: 0;
  }

  &__content {

    // padding: 1rem 0;
  }
}

.progress {
  color: $rappi-green;

  &RT {
    color: $color-rt-cian-primary;
  }
}
</style>
